import {
  Component,
  AfterViewInit,
  OnDestroy,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { DashboardService } from "../dashboard.service";
import { ChartDataService } from "../../../helpers/chart-data.service";
import { am5ChartLicense } from "../../../app.constants";
import { MatSnackBar } from "@angular/material/snack-bar";

@Component({
  selector: "app-bar-chart",
  templateUrl: "./bar-chart.component.html",
  styleUrl: "./bar-chart.component.scss",
})
export class BarChartComponent implements AfterViewInit, OnDestroy, OnChanges, OnInit {
 @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() categoryIds!: number[];
  @Input() companyId!: number;
  isBarGraphZoom: boolean;
  @Input() chartContainerId: string =
    "lineDiv" + Math.random().toString(36).substring(2, 9); // Unique ID for chart container

  private root: am5.Root | undefined;

  constructor(
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private chartDataService: ChartDataService,
    private _snackBar: MatSnackBar
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes in inputs and update the chart
    if (
      changes["categoryIds"] ||
      changes["companyId"] ||
      changes["startDate"]?.currentValue ||
      changes["endDate"]?.currentValue
    ) {
      this.loadChartData();
    }
  }

  ngOnInit(): void {
    const companyIdval = localStorage.getItem("companyId");
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
  }

  ngAfterViewInit() {}

  // Fetch data from API and bind to chart
  loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId,
    };
    if (!params.startDate || !params.endDate) {
      return;
    }
    this.dashboardService.getDashboardLeadFees(params).subscribe(
      (data: any[]) => {
        // Dispose of the existing chart to avoid multiple root errors
        if (this.root) {
          this.root.dispose();
        }

        // Sort the data's XValues correctly and display written labels instead of numbers
        const processedData =
          this.chartDataService.convertXValueFromYearMonthToMonthName(data);

        this.createChart(processedData);
      },
      (error) => {
        this._snackBar.open(
          "Failed to load chart data. Please try again later.",
          "Close",
          {
            duration: 3000,
            horizontalPosition: "center",
            verticalPosition: "top",
          }
        );
        return;
      }
    );
  }

  createChart(data: any[]) {
    this.browserOnly(() => {
      // Get the chart container element
      const element = document.getElementById(this.chartContainerId);
      if (!element) return;

      // Dispose of the existing root if it already exists
      if (this.root) {
        this.root.dispose();
        this.root = undefined; // Clear the reference to avoid re-using it
      }

      am5.addLicense(am5ChartLicense);

      // Create a new root instance
      const root = am5.Root.new(this.chartContainerId);
      this.root = root; // Assign the root to the class property for later disposal

      // Apply themes
      root.setThemes([am5themes_Animated.new(root)]);
      this.root._logo?.dispose(); // Remove the logo to avoid clutter

      // Create chart instance
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panY: false,
          layout: root.verticalLayout,
          wheelX: "none",
          wheelY: "none",
        })
      );

      // Create X-axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 30, // Distance between labels
          }),
          categoryField: "xValue", // Field for category (month)
        })
      );
      xAxis.data.setAll(data);

      const maxYVal = Math.max(...data.map((item) => item.yValue)) + 1000; // Calculate max Y value for scaling
      // Create Y-axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            minGridDistance: 20, // Distance between gridlines
          }),
          min: 0, // Prevent negative values on Y axis
          max: maxYVal, //this.companyId == 4 ? 9000 : 50000, // Maximum value
          strictMinMax: true, // Enforce exact range
          // extraMin: 0.1, // Optional padding
          extraMax: 0.1, // Optional padding
        })
      );

      // Configure gridlines
      yAxis.get("renderer").grid.template.setAll({
        // Optional: Dotted gridlines
        strokeOpacity: 0.1, // Optional: Adjust gridline visibility
        visible: true, // Ensure gridlines are visible
        location: 0, // Start gridlines at 0
      });

      // Configure labels
      yAxis.get("renderer").labels.template.setAll({
        rotation: 0, // Keep labels horizontal
        centerY: am5.percent(50), // Center-align labels
      });

      // Create column series
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "Series",
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "yValue", // Amount of bookings
          categoryXField: "xValue", // Month or category
          clustered: false, // Prevent clustering
        })
      );

      // Set series data
      series.data.setAll(data);

      // Add labels to bars
      series.bullets.push(() =>
        am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            fill: am5.color(0x000000), // Label color
            centerY: am5.percent(0), // Align vertically at the top
            dy: 0, // Position above the bar
            centerX: am5.percent(50), // Center horizontally
            populateText: true, // Ensure dynamic updates
          }),
        })
      );
      let categoryMap = new Map<number, string>();
      if (this.companyId != 4) {
        // Category label mapping
        categoryMap = new Map<number, string>([
          [0, "All"],
          [36, "IKEA meubelmontage"],
          [56, "IKEA B2B Keukeninstallatie"],
          [57, "IKEA B2B Meubelmontage"],
          [63, "IKEA badkamer installatie"],
          [64, "IKEA apparatuur installatie"],
        ]);
      } else {
        categoryMap = new Map<number, string>([
          [0, `€ {valueY} lead fee`],
        ]);
      }
      const allCategoryIds = Array.from(categoryMap.keys());

      const getCategoryLabel = (selectedIds: number[]) => {
        if (selectedIds?.length === 6) {
          return "All";
        } else {
          return selectedIds.map((id) => categoryMap.get(id)).join(",\n");
        }
      };

      xAxis.get("renderer").grid.template.set("visible", false);

      // Configure tooltips
      series.columns.template.setAll({
        tooltipText: this.companyId != 4 ? `{categoryX}: {valueY},\n${getCategoryLabel(
          this.categoryIds ?? []
        )}` : `€ {valueY} lead fee`,
        tooltipY: am5.percent(10), // Tooltip position
        fill: root.interfaceColors.get("primaryButtonDown"),
        fillOpacity: 0.6, // Set column color
      });


      // Add cursor
      chart.set("cursor", am5xy.XYCursor.new(root, {}));
    });
  }

  ngOnDestroy() {
    // Dispose of chart root when component is destroyed
    if (this.root) {
      this.root.dispose();
      this.root = undefined; // Clear the reference to avoid re-using it
    }
  }

  browserOnly(f: () => void) {
    if (typeof window !== "undefined") {
      f();
    }
  }

  
}
